/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import {
  deleteEstate,
  downloadEstateListFile,
  findManyEstates
} from '../../lib/rest/estateAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage, useIntl } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import Filters from '../../../../client/js/components/Filters'
import EstateTransactionFilter from '../filters/EstateTransactionFilter'
import EstateTypeFilter from '../filters/EstateTypeFilter'
import AvailableFilter from '../filters/AvailableFilter'
import ExclusivityFilter from '../filters/ExclusivityFilter'
import PublishedFilter from '../filters/PublishedFilter'
import FurnishedFilter from '../filters/FurnishedFilter'
import EquippedFilter from '../filters/EquippedFilter'
import WithChargesFilter from '../filters/WithChargesFilter'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import EstateList from './EstateList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import { getEstateCreateLink } from '../../lib/links/estateLinks'
import Filter from '../Filter'
import CustomerInput from '../inputs/CustomerInput'
import { handleValueChange } from '../../../../client/js/lib/form'
import { Button, Menu, Switch } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import Toolbar from '../../../../client/js/components/Toolbar'
import QuickMenu from '../QuickMenu'
import useUpdateEstatePhotosWatermarkMutation from './UseUpdateEstatePhotosWatermarkMutation'

export type EstateListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  customerId?: string,
  ownerId?: string,
  transaction?: string[],
  type?: string[],
  available?: string,
  exclusivity?: string,
  published?: string,
  furnished?: string,
  equipped?: string,
  withCharges?: string,
}

export type EstateListCardProps = {
  createLink?: string;
  onQueryChange (query: SetStateAction<EstateListQuery>): void;
  query: EstateListQuery;
}

const initialQuery: EstateListQuery = {
  limit: '10',
  page: '1'
}

function EstateListCard (props: EstateListCardProps) {
  const {
    createLink = getEstateCreateLink(),
    query,
    onQueryChange
  } = props

  const intl = useIntl()

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['estates', queryParams],
    queryFn: () => findManyEstates(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.estates : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search?: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const [deleteForce, setDeleteForce] = useState(false)
  const handleDelete = useCallback((id: number) => deleteEstate(id, { force: deleteForce }), [deleteForce])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  // Download estates list.
  const downloadListQuery = useQuery<unknown, Error>({
    enabled: false,
    queryKey: ['estatesListDownload', queryParams],
    queryFn: () => downloadEstateListFile(queryParams),
    refetchOnWindowFocus: false
  })

  const updateWatermarkMutation = useUpdateEstatePhotosWatermarkMutation()

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <PublishedFilter
          onChange={setFilter}
          value={queryParams.published}
        />
        <EstateTransactionFilter
          onChange={setFilter}
          value={queryParams.transaction}
        />
        <EstateTypeFilter
          onChange={setFilter}
          value={queryParams.type}
        />
        <AvailableFilter
          onChange={setFilter}
          value={queryParams.available}
        />
        <ExclusivityFilter
          onChange={setFilter}
          value={queryParams.exclusivity}
        />
        <FurnishedFilter
          onChange={setFilter}
          value={queryParams.furnished}
        />
        <EquippedFilter
          onChange={setFilter}
          value={queryParams.equipped}
        />
        <WithChargesFilter
          onChange={setFilter}
          value={queryParams.withCharges}
        />
        <Filter>
          <CustomerInput
            onChange={handleValueChange('customerId', setFilter)}
            value={queryParams.customerId}
          />
        </Filter>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:estate']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:estate']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_estate"
                defaultMessage="Créer un bien"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={query.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
        <RequirePerm
          permissions={['read:estate']}
          render={() => (
            <Button
              disabled={!items.length || downloadListQuery.isFetching}
              loading={downloadListQuery.isFetching}
              onClick={() => {
                downloadListQuery.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faDownload}
              />
            </Button>
          )}
        />

        <QuickMenu>
          <RequirePerm
            permissions={['admin']}
            render={() => (
              <Menu.Item
                disabled={updateWatermarkMutation.isPending}
                onClick={() => {
                  updateWatermarkMutation.mutate()
                }}
              >
                <FormattedMessage
                  id="update_watermark_on_photos"
                  defaultMessage="Mettre à jour le watermark des photos"
                />
              </Menu.Item>
            )}
          />
        </QuickMenu>
      </Toolbar>}
    >
      <LoadingHelper
        query={listQuery}
        render={() => (
          <EstateList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={() => {
            setDeleteForce(false)
            toggleDeleteModal()
          }}
          requirePin
        >
          <Switch
            label={intl.formatMessage({
              id: 'delete_related_data',
              defaultMessage: 'Supprimer les données liées'
            })}
            onChange={(ev) => {
              setDeleteForce(ev.target.checked)
            }}
          />
        </DeleteSelectionModal>
        : null}
    </ListCard>
  )
}

export default EstateListCard
